import { Component, inject, Input } from '@angular/core';
import { AbregelungKopplungsTyp } from '../../../../domain/model/abregelung-kopplungs-typ.enum';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ErzeugungsanlageConfig } from '../../erzeugungsanlage.config';
import { AbregelungsinformationenTase2 } from '../../../../domain/model/abregelungsinformationen-tase2.model';
import { FormGroupConfig } from '../../form-config.model';
import { MatValidators } from '../../../../common/material/components/mat-form-field-required.directive';
import {
    AbregelinformationenTase2AdresseComponent,
} from './abregelinformationen-tase2-adresse/abregelinformationen-tase2-adresse.component';
import {
    AbregelungsinformationenTase2Adressen,
} from '../../../../domain/model/abregelungsinformationen-tase2-adressen.model';
import { Feld } from '../../../../domain/model/feld.enum';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { FeldBeschreibungService } from '../../../../service/feldbeschreibung/feldbeschreibung.service';
import { AbregelungInformationen } from '../abregelung-informationen';

@Component({
    selector: 'redispatch-abregelinformationen-tase2-detail',
    templateUrl: './abregelinformationen-tase2-detail.component.html',
    styleUrls: [
        './abregelinformationen-tase2-detail.component.scss',
    ],
    standalone: false,
})
export class AbregelinformationenTase2DetailComponent
    extends AbregelungInformationen<AbregelungsinformationenTase2, AbregelungsinformationenTase2Adressen> {

    public readonly LYT_GAP = ErzeugungsanlageConfig.HORIZONTAL_FX_LAYOUT_GAP;
    public readonly V_LYT_GAP = ErzeugungsanlageConfig.VERTICAL_FX_LAYOUT_GAP;
    public readonly COLS = ErzeugungsanlageConfig.ABREGELINFOS_ADDR_TASE_FX_FLEX_COL;
    public readonly Feld = Feld;

    @Input({ required: true }) disabled: boolean;
    @Input({ required: true }) readonly: boolean;

    private feldBeschreibungService = inject(FeldBeschreibungService);

    constructor() {
        super(inject(UntypedFormBuilder));
    }

    get formGroupConfig(): FormGroupConfig<AbregelungsinformationenTase2> {
        return {
            tase2Verbindungsname: [
                { value: null, disabled: this.disabled || this.readonly },
                [
                    MatValidators.required,
                    Validators.minLength(1),
                    Validators.maxLength(16),
                ],
            ],
            adressen: this.fb.array([
                this.createAddressFormGroup(AbregelungKopplungsTyp.SOLLWERT, true),
                this.createAddressFormGroup(AbregelungKopplungsTyp.TYP_100, true),
                this.createAddressFormGroup(AbregelungKopplungsTyp.TYP_60, true),
                this.createAddressFormGroup(AbregelungKopplungsTyp.TYP_30, true),
                this.createAddressFormGroup(AbregelungKopplungsTyp.TYP_0, true),
                this.createAddressFormGroup(AbregelungKopplungsTyp.WIRKLEISTUNG_P, false),
                this.createAddressFormGroup(AbregelungKopplungsTyp.BLINDLEISTUNG_Q, false),
            ]),
        };
    }

    public get fAdressen(): UntypedFormGroup[] {
        return (this.f.adressen as UntypedFormArray).controls as UntypedFormGroup[];
    }

    public sanitize(infos?: AbregelungsinformationenTase2): AbregelungsinformationenTase2 | undefined {
        if (infos?.adressen) {
            infos.adressen = infos.adressen.filter(value => this.hasAddressValue(value));
        }
        return infos;
    }

    public getHilfetext(feld: Feld): Observable<string | null> {
        return this.feldBeschreibungService.getCachedFeldBeschreibung(feld).pipe(
            map(value => value?.hilfetext),
        );
    }

    protected hasAddressValue(address: AbregelungsinformationenTase2Adressen): boolean {
        return !!address.tase2Objekt && !!address.elementTyp;
    }

    private createAddressFormGroup(typ: AbregelungKopplungsTyp, required: boolean): UntypedFormGroup {
        const form = this.fb.nonNullable.group(
            AbregelinformationenTase2AdresseComponent.formGroupConfig(typ, this.readonly || this.disabled || this.isHidden(typ), required),
            AbregelinformationenTase2AdresseComponent.formOptions(required),
        );
        (form as any).customPassthroughRequired = required;
        return form;
    }
}
